import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import i18nContext from 'components/i18n-context';
import {
  DUKASCOPY_IBAN,
  MANUAL_EUR_BANK_DETAILS,
  MANUAL_OTHER_BANK_DETAILS,
  PAYMENT_PROVIDER
} from 'components/constants';
import Loader from 'components/Loader';
import { useApplicationContext } from 'contexts/ApplicationContext';
import { getCryptoCurrenciesByNetwork, isCryptoProvider } from 'services/utils';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';
import './AddNewAccountScheme.scss';

const AddNewAccountScheme = ({
  isLoading,
  isAddCurrency,
  accountForUpdate,
  currenciesForCreateAccount,
  currenciesForAdd,
  paymentProviders,
  onClose,
  addNewAccount
}) => {
  const i18n = useContext(i18nContext);
  const { constants } = useApplicationContext();
  const [currencySelectOptions, setCurrencySelectOptions] = useState([]);
  const [isClearSearchValue, setIsClearSearchValue] = useState(false);
  const [ibanError, setIbanError] = useState('');

  const form = useFormik({
    initialValues: {
      paymentProvider: '',
      network: '',
      currency: '',
      iban: ''
    },
    onSubmit: async (values) => {
      if (isLoading) return;

      const isIbanRequired = values.paymentProvider === PAYMENT_PROVIDER.CLEAR_JUNCTION.name;
      const trimmedIban = values.iban.trim();

      // Check for internal spaces
      if (trimmedIban && trimmedIban.includes(' ')) {
        setIbanError(i18n.getMessage('accountsTab.modal.addNewAccount.error.noSpacesAllowed'));
        return;
      }

      const isMissingInputData = isIbanRequired ? !trimmedIban : !values.currency;

      if (isMissingInputData) return;

      let accountData = {};

      if (values.paymentProvider !== PAYMENT_PROVIDER.GRAPHEREX.name) {
        accountData.iban = trimmedIban;
        accountData = {
          ...accountData,
          ...(accountForUpdate && { multi_wallet_number: accountForUpdate.multi_wallet_number })
        };
      }

      if (values.paymentProvider === PAYMENT_PROVIDER.GRAPHEREX.name) {
        accountData.network = values.network;
      }
      accountData.currency = values.currency;

      await addNewAccount(values.paymentProvider, accountData);
    }
  });

  const { values, handleSubmit, setFieldValue, handleChange } = form;

  useEffect(() => {
    if (!values.paymentProvider || isCryptoProvider(values.paymentProvider)) {
      setCurrencySelectOptions([]);
    } else {
      setCurrencySelectOptions(
        PAYMENT_PROVIDER[values.paymentProvider].currencies.map((currency) => {
          return {
            key: currency,
            value: currency
          };
        })
      );
    }
  }, [values.paymentProvider]);

  const paymentProviderOptions = paymentProviders.map((provider) => {
    return {
      key: provider,
      value: i18n.getMessage('paymentProvider.' + provider)
    };
  });

  const getCryptoNetworksOptions = () => {
    if (!constants || !constants?.providerProperties || !constants?.providerProperties?.GRAPHEREX) return [];

    const networks = Object.values(constants?.providerProperties?.GRAPHEREX);

    if (!networks || networks?.length === 0) {
      return networks;
    } else {
      return networks.map((network) => {
        return {
          key: network?.code,
          value: network?.name
        };
      });
    }
  };

  const manualEurIbanOptions = Object.keys(MANUAL_EUR_BANK_DETAILS).map((method) => {
    return {
      key: MANUAL_EUR_BANK_DETAILS[method],
      value: MANUAL_EUR_BANK_DETAILS[method]
    };
  });

  const manualOtherIbanOptions = Object.keys(MANUAL_OTHER_BANK_DETAILS).map((method) => {
    return {
      key: MANUAL_OTHER_BANK_DETAILS[method],
      value: MANUAL_OTHER_BANK_DETAILS[method]
    };
  });

  const dukascopyEurIbanOptions = Object.keys(DUKASCOPY_IBAN).map((method) => {
    return {
      key: DUKASCOPY_IBAN[method],
      value: DUKASCOPY_IBAN[method]
    };
  });

  const handleChangeValue = (name, value) => {
    // If it's IBAN field, trim and validate
    if (name === 'iban') {
      const trimmedValue = value.trim();

      if (trimmedValue && trimmedValue.includes(' ')) {
        setIbanError(i18n.getMessage('accountsTab.modal.addNewAccount.error.noSpacesAllowed'));
      } else {
        setIbanError('');
      }

      return setFieldValue(name, trimmedValue);
    }

    return setFieldValue(name, value);
  };

  const handleChangePaymentProviderValue = (name, value) => {
    if (values.network) {
      setFieldValue('network', '');
    }
    setFieldValue('currency', '');
    setIsClearSearchValue(true);
    setIbanError(''); // Clear error when changing provider
    setFieldValue(name, value);
  };

  const handleChangeNetworkValue = (name, value) => {
    setFieldValue(name, value);

    const networkCurrencies = getCryptoCurrenciesByNetwork(value, constants);

    if (networkCurrencies.length > 0 && values.currency && !networkCurrencies.includes(values.currency)) {
      setFieldValue('currency', '');
      setIsClearSearchValue(true);
    }

    setCurrencySelectOptions(
      networkCurrencies.length > 0
        ? networkCurrencies.map((currency) => {
            return {
              key: currency,
              value: currency
            };
          })
        : []
    );
  };

  const handleIbanChange = (e) => {
    const value = e.target.value;
    const trimmedValue = value.trim();

    // Clear error when user starts typing
    if (ibanError) {
      setIbanError('');
    }

    // Check for internal spaces in real-time
    if (trimmedValue && trimmedValue.includes(' ')) {
      setIbanError(i18n.getMessage('accountsTab.modal.addNewAccount.error.noSpacesAllowed'));
    }

    handleChange(e);
  };

  const handleIbanBlur = (e) => {
    const trimmedValue = e.target.value.trim();
    setFieldValue('iban', trimmedValue);

    // Validate on blur
    if (trimmedValue && trimmedValue.includes(' ')) {
      setIbanError(i18n.getMessage('accountsTab.modal.addNewAccount.error.noSpacesAllowed'));
    } else {
      setIbanError('');
    }
  };

  return (
    <div className={'scheme-wrapper scheme-wrapper-add-new-account'}>
      <span className={'scheme-success-header scheme-header-add-new-account'}>
        {isAddCurrency
          ? i18n.getMessage('accountsTab.modal.addNewCurrency.header')
          : i18n.getMessage('accountsTab.modal.addNewAccount.header')}
      </span>

      <form onSubmit={handleSubmit} className={'form-add-new-account-scheme'}>
        <InputDropDown
          className={'table-cell-input'}
          id={'paymentProvider'}
          name={'paymentProvider'}
          label={i18n.getMessage('accountsTab.modal.addNewAccount.input.paymentProvider')}
          options={paymentProviderOptions}
          value={values.paymentProvider}
          onChange={handleChangePaymentProviderValue}
        />
        {isCryptoProvider(values.paymentProvider) && (
          <InputDropDown
            className={'table-cell-input'}
            id={'network'}
            name={'network'}
            label={i18n.getMessage('accountsTab.modal.addNewAccount.input.network')}
            options={getCryptoNetworksOptions()}
            value={values.network}
            onChange={handleChangeNetworkValue}
          />
        )}
        <InputDropDownSearch
          className={'add-new-account-input-dropdown-search'}
          id={'currency'}
          name={'currency'}
          label={i18n.getMessage('accountsTab.modal.addNewAccount.input.currency')}
          options={currencySelectOptions}
          value={values.currency}
          onChange={handleChangeValue}
          isClearSearchValue={isClearSearchValue}
          setIsClearSearchValue={setIsClearSearchValue}
        />
        {(values.paymentProvider === PAYMENT_PROVIDER.CLEAR_JUNCTION.name ||
          values.paymentProvider === PAYMENT_PROVIDER.RYVYL.name ||
          values.paymentProvider === PAYMENT_PROVIDER.GURU_PAY.name) && (
          <Input
            className={'table-cell-input'}
            id={'iban'}
            name={'iban'}
            label={i18n.getMessage('accountsTab.modal.addNewAccount.input.iban')}
            value={values.iban}
            onChange={handleIbanChange}
            onBlur={handleIbanBlur}
            error={ibanError}
          />
        )}
        {values.paymentProvider === PAYMENT_PROVIDER.MANUAL.name && (
          <InputDropDown
            className={'table-cell-input'}
            id={'iban'}
            name={'iban'}
            label={i18n.getMessage('accountsTab.modal.addNewAccount.input.iban')}
            options={values.currency === 'EUR' ? manualEurIbanOptions : manualOtherIbanOptions}
            value={values.iban}
            onChange={handleChangeValue}
          />
        )}

        {values.paymentProvider === PAYMENT_PROVIDER.DUKASCOPY.name && values.currency === 'EUR' && (
          <InputDropDown
            className={'table-cell-input'}
            id={'iban'}
            name={'iban'}
            label={i18n.getMessage('accountsTab.modal.addNewAccount.input.iban')}
            options={dukascopyEurIbanOptions}
            value={values.iban}
            onChange={handleChangeValue}
          />
        )}

        <div className={'scheme-wrapper-buttons add-new-account-scheme-buttons-wrapper'}>
          <Button className={'scheme-button'} onClick={onClose} type={'outline'} isDisabled={isLoading}>
            {i18n.getMessage('accountsTab.modal.addNewAccount.action.back')}
          </Button>
          <Button
            roleType={'submit'}
            className={'scheme-button'}
            isDisabled={
              isLoading ||
              ibanError ||
              (values.paymentProvider === PAYMENT_PROVIDER.CLEAR_JUNCTION.name
                ? !values.iban || !values.iban.trim()
                : !values.currency)
            }
          >
            {isLoading ? <Loader /> : i18n.getMessage('accountsTab.modal.addNewAccount.action.create')}
          </Button>
        </div>
      </form>
    </div>
  );
};

AddNewAccountScheme.propTypes = {
  isLoading: PropTypes.bool,
  isAddCurrency: PropTypes.bool,
  accountForUpdate: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  currenciesForCreateAccount: PropTypes.array,
  currenciesForAdd: PropTypes.array,
  paymentProviders: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  addNewAccount: PropTypes.func.isRequired
};

export default AddNewAccountScheme;
